"use client";

import { Card, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { removeNonIntegersFromString } from "@/helpers/string-helpers";
import { ICampaignLog } from "@/interfaces/campaign-logs.interface";
import { CampaignLogsMessages } from "@/messages/campaigns/logs/campaign-logs.messages";
import { CampaignLogStatus } from "@/types/campaign-log.types";
import { FilterType, IFilter } from "@/types/filter.types";

import { CampaignLogFilterKey } from "../../types";

type Props = {
  messages: CampaignLogsMessages;
};

const GRID_SIZES = {
  LARGE: { lg: 4, md: 6, sm: 12, xs: 12 },
  SMALL: { lg: 2, md: 2, sm: 2, xs: 12 },
} as const;

const STATUS_VALUES = {
  EMPTY: "",
} as const;

function FiltersForm({ messages }: Props) {
  const { values, setValues, submitForm } = useFormikContext<IFilter<ICampaignLog>[]>();
  const [phoneInputValue, setPhoneInputValue] = useState<string>("");

  const getValueFromProperty = (
    property: string,
    filterValues: IFilter<ICampaignLog>[]
  ): IFilter<ICampaignLog> | undefined => {
    const filter = filterValues.find((f) => f.property === property);
    return filter;
  };

  const handleFilterChange = (
    property: CampaignLogFilterKey,
    value: string | number | boolean | null,
    filterType: FilterType
  ) => {
    setValues((prevFilters) => {
      const currentFilters = [...prevFilters];

      const existingFilterIndex = currentFilters.findIndex((f) => f.property === property);
      if (existingFilterIndex !== -1) {
        currentFilters.splice(existingFilterIndex, 1);
      }

      const isEmptyValue =
        value === null || value === "" || (typeof value === "number" && isNaN(value));
      if (isEmptyValue) {
        return currentFilters;
      }

      const newFilter: IFilter<ICampaignLog> = {
        property: property as keyof ICampaignLog,
        filterType: filterType,
        value: value,
      };

      return [...currentFilters, newFilter];
    });
  };

  const handleStatusChange = (selectedValue: string) => {
    const statusValue = selectedValue === STATUS_VALUES.EMPTY ? null : selectedValue;
    handleFilterChange("lastStatus", statusValue, FilterType.Equals);
  };

  const handlePhoneChange = (value: string) => {
    const formattedValue = formatPhoneInput(value);
    setPhoneInputValue(formattedValue);

    if (!value || value.replace(/[+\s()_-]/g, "").length === 0) {
      handleFilterChange("contactMobilePhone", null, FilterType.Contains);
      return;
    }

    const numericValue = removeNonIntegersFromString(value);
    if (numericValue.length === 0) {
      handleFilterChange("contactMobilePhone", null, FilterType.Contains);
      return;
    }

    handleFilterChange("contactMobilePhone", numericValue, FilterType.Contains);
  };

  const handleClearFilters = () => {
    setValues([]);
    setPhoneInputValue("");
  };

  const formatPhoneInput = (newValue: string): string => {
    const mask = "+__ (__) _____-____";
    const numbersOnly = newValue.replace(/[^\d]/g, "");

    const limitedNumbers = numbersOnly.slice(0, 13);

    if (limitedNumbers.length === 0) {
      return "";
    }

    let formatted = mask;
    let numberIndex = 0;

    formatted = formatted.replace(/_/g, () => {
      if (numberIndex < limitedNumbers.length) {
        return limitedNumbers[numberIndex++];
      }
      return "_";
    });

    return formatted;
  };

  return (
    <Card>
      <MDBox p={2}>
        <Grid container spacing={2} alignItems="stretch" wrap="wrap">
          <Grid size={GRID_SIZES.LARGE}>
            <TextField
              name="contactName"
              label={messages.contactName}
              onChange={(e) =>
                handleFilterChange("contactName", e.target.value, FilterType.Contains)
              }
              value={getValueFromProperty("contactName", values)?.value ?? ""}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>

          <Grid size={GRID_SIZES.LARGE}>
            <TextField
              name="contactMobilePhone"
              label={messages.contactPhone}
              placeholder="+55 (11) 99999-9999"
              value={phoneInputValue}
              onChange={(e) => handlePhoneChange(e.target.value)}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </Grid>

          <Grid size={GRID_SIZES.LARGE}>
            <FormControl fullWidth>
              <InputLabel shrink>{messages.status}</InputLabel>
              <Select
                value={String(
                  getValueFromProperty("lastStatus", values)?.value ?? STATUS_VALUES.EMPTY
                )}
                onChange={(e) => handleStatusChange(e.target.value)}
                label={messages.status}
                displayEmpty
                inputProps={{ "aria-label": messages.status }}
              >
                <MenuItem value={STATUS_VALUES.EMPTY}>
                  {messages.selectStatusPlaceholder || "Selecione..."}
                </MenuItem>
                <MenuItem value={CampaignLogStatus.SENT}>{messages.statusSent}</MenuItem>
                <MenuItem value={CampaignLogStatus.DELIVERED}>{messages.statusDelivered}</MenuItem>
                <MenuItem value={CampaignLogStatus.READ}>{messages.statusRead}</MenuItem>
                <MenuItem value={CampaignLogStatus.CLICKED}>{messages.statusClicked}</MenuItem>
                <MenuItem value={CampaignLogStatus.FAILED}>{messages.statusFailed}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <Grid container spacing={2} mt={0}>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="light" onClick={handleClearFilters} fullWidth>
                  {messages.clearFilterButton}
                </MDButton>
              </Grid>
              <Grid size={GRID_SIZES.SMALL}>
                <MDButton variant="gradient" color="dark" onClick={submitForm} fullWidth>
                  {messages.filterButton}
                </MDButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default FiltersForm;
